import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import { type ReactElement, useState } from 'react'
import type { InputInFormProps } from '~/components/Form/Input/input'
import {
  errorMessage,
  inputError,
  inputField,
  inputLabel,
  inputWrapper,
  textarea,
  textareaInfo,
  textareaInfoHeader,
  textareaInfoTrigger
} from './TextInput.css'

type TextareaInputProps = React.HTMLProps<HTMLTextAreaElement> &
  InputInFormProps & {
    label?: string
    name?: string
    error?: null | { message: string }
    contextInfo?: string
  }

export const TextareaInput = ({
  control,
  register,
  label,
  name,
  error = null,
  onChange,
  watch,
  value,
  setValue,
  className,
  contextInfo,
  ...rest
}: TextareaInputProps): ReactElement => {
  if (register === undefined || control === undefined || name === undefined) {
    throw new Error('TextareaInput must be used inside a Form component')
  }
  const [showInfo, setShowInfo] = useState(false)
  const { onChange: onChangeRegister, ...registerRest } = register(name)
  return (
    <div className={inputWrapper}>
      {label && (
        <label htmlFor={name} className={inputLabel}>
          {label}
        </label>
      )}
      <div className={inputField}>
        <textarea
          className={clsx(textarea, error !== null && inputError, className)}
          aria-invalid={error !== null ? 'true' : 'false'}
          aria-label={label}
          onChange={(e) => {
            if (onChange) {
              onChange(e)
            }
            onChangeRegister(e)
          }}
          value={value}
          {...rest}
          {...registerRest}
        />
        {contextInfo && (
          <div onMouseLeave={() => setShowInfo(false)}>
            <AnimatePresence>
              <span
                className={textareaInfoTrigger}
                onMouseEnter={() => setShowInfo(true)}
                key="trigger"
              >
                <FontAwesomeIcon icon={faInfoCircle} />
              </span>
              {showInfo && (
                <motion.div
                  initial={{ opacity: 0, x: 10, y: 10 }}
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  exit={{ opacity: 0, x: 10, y: 10 }}
                  className={textareaInfo}
                  key="info"
                >
                  <div className={textareaInfoHeader}>
                    <FontAwesomeIcon icon={faInfoCircle} /> Informations
                  </div>
                  {contextInfo}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
      {error !== null && (
        <div className={errorMessage} role="alert">
          {error.message}
        </div>
      )}
    </div>
  )
}
